/**
 * Endpoints du domaine admin (administration & double validation) — source
 * canonique. Chemins copiés TELS QUELS de la collection Postman / du guide
 * §6.8 (admin-service, préfixe `/gw/admin`) et §6.1 (endpoints propres au
 * gateway — SANS préfixe `/gw/`). Ne jamais inventer un chemin.
 * `@/core/api/endpoints` ré-exporte ces valeurs : ajouter les routes ICI.
 */
export const adminEndpoints = {
  approvalRequests: {
    /**
     * POST — flashpay-superadmin, flashpay-admin, flashpay-service. Ouvre une
     * demande d'approbation au statut PENDING (guide §5.4). `resourceId` doit
     * désigner exactement la ressource qui sera modifiée.
     */
    add: "/gw/admin/api/v1/ApprovalRequests/add",
    /** POST — file d'attente paginée, corps `ListRequest<ApprovalRequestFilter>`. */
    all: "/gw/admin/api/v1/ApprovalRequests/all",
    /** GET — état d'une demande (interrogé en direct par fee-engine, ledger, kyc). */
    detail: (approvalRequestId: string) =>
      `/gw/admin/api/v1/ApprovalRequests/${approvalRequestId}`,
    /**
     * POST — flashpay-superadmin, flashpay-admin. Règle des quatre yeux :
     * approbateur ≠ demandeur, sinon 422 (message backend à afficher).
     */
    approve: (approvalRequestId: string) =>
      `/gw/admin/api/v1/ApprovalRequests/${approvalRequestId}/approve`,
    /** POST — flashpay-superadmin, flashpay-admin. Rejette une demande PENDING. */
    reject: (approvalRequestId: string) =>
      `/gw/admin/api/v1/ApprovalRequests/${approvalRequestId}/reject`,
    /**
     * POST — réservé au demandeur d'origine (403 sinon).
     * `?requesterKeycloakId=` optionnel.
     */
    cancel: (approvalRequestId: string) =>
      `/gw/admin/api/v1/ApprovalRequests/${approvalRequestId}/cancel`,
    /**
     * POST — flashpay-superadmin, flashpay-service. Acquitte l'exécution
     * effective d'une demande APPROVED (passage en APPLIED).
     */
    markApplied: (approvalRequestId: string) =>
      `/gw/admin/api/v1/ApprovalRequests/${approvalRequestId}/mark-applied`,
    /**
     * POST — flashpay-superadmin, flashpay-service. Signale l'échec
     * d'exécution d'une demande APPROVED (motif inclus).
     */
    markFailed: (approvalRequestId: string) =>
      `/gw/admin/api/v1/ApprovalRequests/${approvalRequestId}/mark-failed`,
  },
  auditLogs: {
    /**
     * POST — flashpay-superadmin, flashpay-admin. Journal d'audit consolidé
     * paginé (état avant/après), corps `ListRequest<AuditLogFilter>`.
     */
    all: "/gw/admin/api/v1/AuditLogs/all",
    /**
     * POST — flashpay-superadmin uniquement. ⚙️ Point d'entrée de
     * remplacement du consommateur Kafka d'audit (déduplication par eventId).
     */
    simulateAuditEvent: "/gw/admin/api/v1/AuditLogs/simulate-audit-event",
  },
  systemConfigs: {
    /** GET — authentifié. Toutes les clés de configuration système. */
    all: "/gw/admin/api/v1/SystemConfigs",
    /** GET — authentifié. Valeur d'une clé de configuration. */
    detail: (key: string) => `/gw/admin/api/v1/SystemConfigs/${key}`,
    /**
     * PUT — flashpay-superadmin, flashpay-admin. Crée ou met à jour une clé
     * (l'auteur de la modification est tracé via `updatedByKeycloakId`).
     */
    update: (key: string) => `/gw/admin/api/v1/SystemConfigs/${key}`,
  },
  /**
   * ApiClients — endpoints PROPRES AU GATEWAY (§6.1) : aucun préfixe de
   * routage `/gw/`. Rôles : flashpay-superadmin, flashpay-admin.
   */
  apiClients: {
    /** POST — crée un client API partenaire (la `apiKey` n'est retournée qu'ici). */
    add: "/api/v1/ApiClients/add",
    /** POST — liste paginée, corps `ListRequest<ApiClientFilter>`. */
    all: "/api/v1/ApiClients/all",
    /** GET — détail d'un client API (scopes, quota par minute). */
    detail: (apiClientId: string) => `/api/v1/ApiClients/${apiClientId}`,
    /** POST — révoque un client API. */
    revoke: (apiClientId: string) => `/api/v1/ApiClients/${apiClientId}/revoke`,
    /** PUT — remplace les scopes d'un client API. */
    scopes: (apiClientId: string) => `/api/v1/ApiClients/${apiClientId}/scopes`,
  },
  deviceSessionKeys: {
    /**
     * POST — authentifié (aucun rôle particulier). ⚙️ Échange une clé
     * publique device contre la clé publique serveur (endpoint gateway).
     */
    exchange: "/api/v1/DeviceSessionKeys/exchange",
  },
} as const;
